import type { SubListType } from '@/domains/resource-data/ApiFolderSubListBuilder';
import type { ApiFolderServerType } from '@/services/api-folder/apiFolder';
import type { CustomEndpointServerType } from '@/services/custom-endpoint/customEndpoint';
import type { DataSchemaServerType } from '@/services/data-schema/dataSchema';
import type { DocServerType } from '@/services/doc/doc';
import type { HttpApiServerType } from '@/services/http-api/httpApi';
import type { SecuritySchemeServerType } from '@/services/security-scheme/securityScheme';
import type { SocketIOServerType } from '@/services/socket-io-api/socketIOApi';
import type { WebSocketApiServerType } from '@/services/websocket-api/webSocketApi';
import type { InheritAuthInfoResult } from '@tarslib/base/lib/AuthBuilder';
import type { ModelType } from '@tarslib/types/lib/common';
import type { IHttpFolderMap } from '@tarslib/types/lib/httpFolder';
import type { LandingPage, LandingPageType } from '@tarslib/types/lib/publishedProject';
import type { JSONSchema7 } from 'json-schema';
import type { Any } from './any';
import type { ApiFieldList } from './ApiFieldList';

export type ResourceDocData = {
  type: ModelType.Doc;
  data: DocServerType;
  extraData: {
    apiFieldList: ApiFieldList[];
    dataSchemaDefinitions: DataSchemaServerType;
  };
};

export type ResourceApiFolderData = {
  type: ModelType.ApiFolder;
  data: ApiFolderServerType;
  extraData: {
    subList: SubListType[];
    apiFieldList: ApiFieldList[];
    dataSchemaDefinitions: DataSchemaServerType;
  };
};

export type ResourceCustomEndpointData = {
  type: ModelType.CUSTOM_ENDPOINT;
  data: CustomEndpointServerType;
  extraData: {
    apiFieldList: ApiFieldList[];
    dataSchemaDefinitions: DataSchemaServerType;
  };
};

export type ResourceHttpApiData = {
  type: ModelType.HttpApi;
  data: HttpApiServerType;
  extraData: {
    apiFieldList: ApiFieldList[];
    dataSchemaDefinitions: DataSchemaServerType;
    requestCollection: typeof Any;
    defaultRequestSampleCode:
      | {
          code: string;
          language: string;
          variant: string;
          codeBlockLanguage: string;
          prompt: string | undefined;
        }
      | undefined;
    serverId: string;
    securitySchemeMap: SecuritySchemeServerType;
    inheritAuthInfo: InheritAuthInfoResult | undefined | null;
    oasExtensions: string;
    apiFolderMap: IHttpFolderMap;
    callbacks?: CallbacksData;
  };
};

export type ResourceWebSocketApiData = {
  type: ModelType.WebSocketApi;
  data: WebSocketApiServerType;
  extraData: {
    apiFieldList: ApiFieldList[];
    dataSchemaDefinitions: DataSchemaServerType;
    serverId: string;
  };
};

export type ResourceSocketIOApiData = {
  type: ModelType.SOCKET_IO_API;
  data: SocketIOServerType;
  extraData: {
    apiFieldList: ApiFieldList[];
    dataSchemaDefinitions: DataSchemaServerType;
  };
};

export type ResourceDataSchemaData = {
  type: ModelType.ApiSchema;
  data:
    | (JSONSchema7 & {
        name: string | undefined;
      })
    | null;
  extraData: {
    apiFieldList: ApiFieldList[];
    dataSchemaDefinitions: DataSchemaServerType;
    example: string;
    description?: string;
    displayName?: string;
  };
};

export const ResourceNotFound = 'NotFound' as const;

export type ResourceNotFoundData = {
  type: typeof ResourceNotFound;
  data: undefined;
  extraData: {
    apiFieldList: ApiFieldList[];
    dataSchemaDefinitions: DataSchemaServerType;
  };
};

export const ResourceCustomLandingPage = 'CustomLandingPage' as const;
export type ResourceCustomLandingPageData = {
  type: typeof ResourceCustomLandingPage;
  data:
    | (LandingPage['markdown'] & {
        type: LandingPageType.MARKDOWN;
      })
    | (LandingPage['html'] & {
        type: LandingPageType.HTML;
      });
  extraData: {
    apiFieldList: ApiFieldList[];
    dataSchemaDefinitions: DataSchemaServerType;
  };
};

export type ResourceData =
  | ResourceNotFoundData
  | ResourceDocData
  | ResourceApiFolderData
  | ResourceCustomEndpointData
  | ResourceHttpApiData
  | ResourceWebSocketApiData
  | ResourceSocketIOApiData
  | ResourceDataSchemaData
  | ResourceCustomLandingPageData;

export type CallbackItem = HttpApiServerType & {
  extraData: Pick<
    ResourceHttpApiData['extraData'],
    'requestCollection' | 'defaultRequestSampleCode'
  >;
};

export type CallbacksData = Record<string, CallbackItem[]>;
